const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export interface ApiValidationIssue {
  path: string;
  message: string;
}

export class ApiError extends Error {
  public issues?: ApiValidationIssue[];
  public errorCode?: string;
  public data?: unknown;

  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';

    // Try to parse structured error body (NestJS BadRequestException with issues array)
    try {
      const parsed = JSON.parse(message);
      if (parsed?.message) {
        // NestJS wraps object exceptions: message can be string or nested object
        const msg = parsed.message;
        if (typeof msg === 'object' && msg !== null) {
          this.message = msg.message ?? message;
          this.errorCode = msg.errorCode;
          this.data = msg.data;
        } else {
          this.message = msg;
        }
      }
      if (Array.isArray(parsed?.issues)) {
        this.issues = parsed.issues;
      }
      if (parsed?.errorCode && !this.errorCode) {
        this.errorCode = parsed.errorCode;
      }
      if (parsed?.data && !this.data) {
        this.data = parsed.data;
      }
    } catch {
      // Not JSON — keep raw message
    }
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown;
};

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers: customHeaders, ...rest } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  const config: RequestInit = {
    ...rest,
    headers,
    credentials: 'include',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  };

  const response = await fetch(`${BASE_URL}${endpoint}`, config);

  if (!response.ok) {
    const errorBody = await response.text();
    throw new ApiError(
      response.status,
      errorBody || `Request failed with status ${response.status}`,
    );
  }

  // Handle 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};
